import React, { useState, useEffect } from 'react'
import { RefreshCw, Download, Heart, Share2, Eye, User, Send, Sliders, Paperclip, X, Image as ImageIcon } from 'lucide-react'
import { getPublicImages } from '../../lib/supabase'
import CommentSection from '../Comments/CommentSection'
import { useDeviceType } from '../../hooks/useDeviceType'
import { FilterOptions } from '../../types'

interface PublicImage {
  id: string
  title: string
  description?: string
  prompt_text: string
  image_url: string
  is_public: boolean
  is_nsfw: boolean
  likes_count: number
  views_count: number
  created_at: string
  profiles?: {
    username?: string
    display_name?: string
  }
}

interface DiscoverPanelProps {
  prompt: string
  onPromptChange: (value: string) => void
  onGenerate: () => void
  isGenerating: boolean
  showFilters: boolean
  onToggleFilters: () => void
  canGenerate: boolean
  uploadedImages: File[]
  onImagesChange: (images: File[]) => void
  onSwitchToCreate: () => void
}

const DiscoverPanel: React.FC<DiscoverPanelProps> = ({
  prompt,
  onPromptChange,
  onGenerate,
  isGenerating,
  showFilters,
  onToggleFilters,
  canGenerate,
  uploadedImages,
  onImagesChange,
  onSwitchToCreate
}) => {
  const { isMobile } = useDeviceType()
  const [images, setImages] = useState<PublicImage[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedImage, setSelectedImage] = useState<PublicImage | null>(null)
  const [refreshing, setRefreshing] = useState(false)

  useEffect(() => {
    loadPublicImages()
  }, [])

  const loadPublicImages = async () => {
    try {
      setLoading(true)
      const publicImages = await getPublicImages(50)
      setImages(publicImages as PublicImage[])
    } catch (error) {
      console.error('Error loading public images:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleRefresh = async () => {
    setRefreshing(true)
    await loadPublicImages()
    setRefreshing(false)
  }

  const handleImageClick = (image: PublicImage) => {
    setSelectedImage(image)
  }

  const handleDownload = async (image: PublicImage) => {
    try {
      const link = document.createElement('a')
      link.href = image.image_url
      link.download = `toefetti_${image.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.png`
      link.target = '_blank'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } catch (error) {
      console.error('Error downloading image:', error)
    }
  }

  const handleFileSelect = (files: FileList | null) => {
    if (!files || !onImagesChange) return
    
    const imageFiles = Array.from(files).filter(file => 
      file.type.startsWith('image/')
    )
    
    if (imageFiles.length > 0) {
      const newImages = [...uploadedImages, ...imageFiles].slice(0, 3) // Max 3 images
      onImagesChange(newImages)
    }
  }

  const handleRemoveImage = (index: number) => {
    if (!onImagesChange) return
    const newImages = uploadedImages.filter((_, i) => i !== index)
    onImagesChange(newImages)
  }

  const handleGenerateFromDiscover = () => {
    // Switch to Create tab and trigger generation
    onSwitchToCreate()
    setTimeout(() => {
      onGenerate()
    }, 100) // Small delay to ensure tab switch completes
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60)
    
    if (diffInHours < 1) {
      return 'Just now'
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)}h ago`
    } else if (diffInHours < 168) {
      return `${Math.floor(diffInHours / 24)}d ago`
    } else {
      return date.toLocaleDateString()
    }
  }

  // Compact grid layout for panel
  const CompactGridLayout = () => {
    if (images.length === 0) return null

    return (
      <div className="grid grid-cols-2 gap-2 p-4">
        {images.map((image) => (
          <div
            key={image.id}
            className="relative group cursor-pointer overflow-hidden rounded-lg bg-gray-100 aspect-square"
            onClick={() => handleImageClick(image)}
          >
            <img
              src={image.image_url}
              alt={image.title}
              className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-105"
              loading="lazy"
            />
            
            {/* Hover overlay */}
            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all duration-200 flex items-center justify-center">
              <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 text-white text-center">
                <div className="flex items-center justify-center space-x-2 mb-1">
                  <div className="flex items-center space-x-1">
                    <Heart className="w-3 h-3" />
                    <span className="text-xs">{image.likes_count}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Eye className="w-3 h-3" />
                    <span className="text-xs">{image.views_count}</span>
                  </div>
                </div>
                <p className="text-xs font-medium truncate px-1">{image.title}</p>
              </div>
            </div>

            {/* User avatar in corner */}
            <div className="absolute top-1 left-1 w-6 h-6 bg-white bg-opacity-90 rounded-full flex items-center justify-center shadow-sm">
              <User className="w-3 h-3 text-gray-600" />
            </div>
          </div>
        ))}
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Header */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-lg flex items-center justify-center">
              <Eye className="w-4 h-4 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">Discover</h2>
              <p className="text-xs text-gray-600">Explore & create</p>
            </div>
          </div>
          
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center space-x-1 px-2 py-1 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 disabled:opacity-50 text-sm"
          >
            <RefreshCw className={`w-3 h-3 ${refreshing ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
        </div>

        {/* Creation Bar */}
        <div className="space-y-3">
          {/* Image Upload Area */}
          {uploadedImages.length > 0 && (
            <div>
              <div className="flex items-center space-x-1 mb-2">
                <ImageIcon className="w-3 h-3 text-gray-600" />
                <span className="text-xs font-medium text-gray-700">Reference Images</span>
                <span className="text-xs text-gray-500">({uploadedImages.length}/3)</span>
              </div>
              <div className="flex flex-wrap gap-1">
                {uploadedImages.map((file, index) => (
                  <div key={index} className="relative group">
                    <div className="w-12 h-12 rounded-md overflow-hidden border border-gray-200 bg-gray-100">
                      <img
                        src={URL.createObjectURL(file)}
                        alt={`Upload ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <button
                      onClick={() => handleRemoveImage(index)}
                      className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                    >
                      <X className="w-2 h-2" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Input Bar */}
          <div className="flex items-center space-x-2">
            <div className="flex-1 relative">
              <input
                type="text"
                value={prompt}
                onChange={(e) => onPromptChange(e.target.value)}
                placeholder={!canGenerate 
                  ? "Sign up to generate..." 
                  : uploadedImages.length > 0
                  ? "Modify your images..."
                  : "Describe what you want..."
                }
                disabled={!canGenerate}
                className={`w-full px-3 py-2 pr-8 border border-gray-300 rounded-lg text-sm transition-colors ${
                  !canGenerate 
                    ? 'bg-gray-50 border-gray-200 cursor-not-allowed' 
                    : 'focus:ring-2 focus:ring-primary-500 focus:border-transparent'
                }`}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !isGenerating && canGenerate) {
                    handleGenerateFromDiscover()
                  }
                }}
              />
              
              {/* Attachment Button */}
              <button
                onClick={() => {
                  if (!canGenerate || uploadedImages.length >= 3) return
                  const input = document.createElement('input')
                  input.type = 'file'
                  input.accept = 'image/*'
                  input.multiple = true
                  input.onchange = (e) => handleFileSelect((e.target as HTMLInputElement).files)
                  input.click()
                }}
                disabled={!canGenerate || uploadedImages.length >= 3}
                className={`absolute right-2 top-1/2 transform -translate-y-1/2 p-1 rounded transition-colors ${
                  !canGenerate || uploadedImages.length >= 3
                    ? 'text-gray-300 cursor-not-allowed'
                    : 'text-gray-500 hover:text-primary-600 hover:bg-primary-50'
                }`}
                title={uploadedImages.length >= 3 ? 'Maximum 3 images allowed' : 'Upload reference image'}
              >
                <Paperclip className="w-3 h-3" />
              </button>
            </div>
            
            <button
              onClick={onToggleFilters}
              className={`p-2 rounded-lg border transition-colors ${
                showFilters 
                  ? 'border-primary-500 bg-primary-50 text-primary-600' 
                  : 'border-gray-300 hover:border-gray-400 text-gray-600'
              }`}
              title="Toggle Filters"
            >
              <Sliders className="w-4 h-4" />
            </button>
            
            <button
              onClick={handleGenerateFromDiscover}
              disabled={isGenerating || !canGenerate}
              className={`px-3 py-2 rounded-lg font-medium transition-all duration-200 flex items-center space-x-1 text-sm ${
                !canGenerate
                  ? 'bg-red-500 hover:bg-red-600 text-white'
                  : isGenerating
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-gradient-to-r from-primary-500 to-secondary-500 text-white hover:from-primary-600 hover:to-secondary-600'
              }`}
            >
              {isGenerating ? (
                <>
                  <div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Creating...</span>
                </>
              ) : !canGenerate ? (
                <span>Sign Up</span>
              ) : (
                <>
                  <Send className="w-3 h-3" />
                  <span>Create</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <div className="text-center">
              <div className="w-6 h-6 border-2 border-primary-200 border-t-primary-500 rounded-full animate-spin mx-auto mb-2"></div>
              <p className="text-sm text-gray-600">Loading images...</p>
            </div>
          </div>
        ) : images.length === 0 ? (
          <div className="flex items-center justify-center h-32">
            <div className="text-center">
              <Eye className="w-8 h-8 text-gray-300 mx-auto mb-2" />
              <h3 className="text-sm font-semibold text-gray-900 mb-1">No public images yet</h3>
              <p className="text-xs text-gray-600">Be the first to share!</p>
            </div>
          </div>
        ) : (
          <CompactGridLayout />
        )}
      </div>

      {/* Image Detail Modal */}
      {selectedImage && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full h-full max-w-4xl max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-full flex items-center justify-center">
                  <span className="text-white font-medium text-sm">
                    {selectedImage.profiles?.display_name?.[0] || selectedImage.profiles?.username?.[0] || '?'}
                  </span>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">
                    {selectedImage.profiles?.display_name || selectedImage.profiles?.username || 'Anonymous'}
                  </h3>
                  <p className="text-sm text-gray-500">{formatDate(selectedImage.created_at)}</p>
                </div>
              </div>
              
              <button
                onClick={() => setSelectedImage(null)}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto">
              <div className={`${isMobile ? 'block' : 'grid grid-cols-2'} h-full`}>
                {/* Image */}
                <div className="flex items-center justify-center bg-gray-50 p-4">
                  <img
                    src={selectedImage.image_url}
                    alt={selectedImage.title}
                    className="max-w-full max-h-full object-contain rounded-lg"
                  />
                </div>

                {/* Details and Comments */}
                <div className="flex flex-col h-full">
                  {/* Image Info */}
                  <div className="p-4 border-b border-gray-200">
                    <h4 className="font-bold text-lg text-gray-900 mb-2">{selectedImage.title}</h4>
                    {selectedImage.description && (
                      <p className="text-gray-600 mb-3">{selectedImage.description}</p>
                    )}
                    <p className="text-sm text-gray-500 bg-gray-50 p-3 rounded-lg mb-3">
                      "{selectedImage.prompt_text}"
                    </p>
                    
                    {/* Stats */}
                    <div className="flex items-center space-x-4 text-sm text-gray-500">
                      <div className="flex items-center space-x-1">
                        <Heart className="w-4 h-4" />
                        <span>{selectedImage.likes_count}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Eye className="w-4 h-4" />
                        <span>{selectedImage.views_count}</span>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center space-x-2 mt-4">
                      <button
                        onClick={() => handleDownload(selectedImage)}
                        className="flex items-center space-x-2 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600"
                      >
                        <Download className="w-4 h-4" />
                        <span>Download</span>
                      </button>
                      <button className="flex items-center space-x-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">
                        <Share2 className="w-4 h-4" />
                        <span>Share</span>
                      </button>
                    </div>
                  </div>

                  {/* Comments */}
                  <div className="flex-1 p-4 overflow-y-auto">
                    <CommentSection 
                      imageId={selectedImage.id} 
                      isPublic={selectedImage.is_public} 
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default DiscoverPanel 